import PageWrapper from "@components/layouts/PageWrapper";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { Toast } from "@helpers/popups/Toast";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import { useUpdatePrimarySaleOrderMutation } from "@states/primary-sale-order/primary-sale-order.api";
import BackButton from "@components/buttons/BackButton";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { saleOrderSchema } from "@helpers/validation-schemas/primary-sale-order/sale-order.schema";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomDateField from "@components/form/CustomDateField";
import CustomSearchModalChooseField from "@components/form/CustomSearchModalChooseField";
import useGetAllCustomers from "@hooks/customer/useGetAllCustomers";
import CustomSelectField from "@components/form/CustomSelectField";
import CustomTextAreaField from "@components/form/CustomTextAreaField";
import { useLazyGetProductByBarcodeQuery } from "@states/product/product.api";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@states/store";
import { thousandSeperator } from "@utils/general/digit-separators";
import CustomInputField from "@components/form/CustomInputField";
import CustomButton from "@components/buttons/CustomButton";
import { productDetailColumns } from "@helpers/columns/primary-sale-order/product-detail.columns";
import useGetOnePrimarySaleorder from "@hooks/primary-sales-order/useGetOnePrimarySaleOrder";
import {
  addFormValues,
  addProductDetail,
  removeAllProductDetail,
  removeProductDetail,
  resetFormValues,
  updateProductDetail,
} from "@states/common/common.slice";
import { TSuccessResponse } from "@type-defs/general/TSuccessResponse";
import { TProduct } from "@type-defs/product/TProduct";
import { calculatePercentage } from "@utils/general/cal-percent";
import {
  useGetAllPaymentTermsQuery,
  useGetWarehousesByUserQuery,
  useLazyGetProductDetailByWarehouseQuery,
  useGetAllSaleTypeQuery,
  useGetCustomerByUserQuery,
  useLazyGetAllPromotionNameListQuery,
  useLazyGetAllPromotionProductListQuery,
  useLazyGetAllWayWarehouseQuery,
} from "@states/common/common.api";
import useGetAllPaymentTypes from "@hooks/common/useGetAllPaymentTypes";
import { useDraggable } from "react-use-draggable-scroll";
import useGetAllProducts from "@hooks/product/useGetAllProducts";
import useGetOneSecondarySalesorder from "@hooks/secondary-sales-order/useGetOneSecondarySaleOrder";
import { SECONDARY_SALE_ORDER } from "@utils/constants/secondary-sale-order.constant";
import {
  useGetCustomerQuery,
  useLazyGetCustomerQuery,
} from "@states/customer/customer.api";
import { selectCurrentUser } from "@states/auth/auth.slice";
import { LOCAL_STORAGE_KEYS } from "@utils/constants/local-storeage-keys.constants";
import { PRIMARY_SALE_ORDER } from "@utils/constants/primary-sale-order.constants";
import { useUpdateSecondarySaleOrderMutation } from "@states/secondary-sale-order/secondary-sale-order.api";
import DeleteButton from "@components/buttons/DeleteButton";
import SaleOrderPromotionDetailModal from "@components/modals/sales-order/SaleOrderPromotionDetailModal";
import PromotionIcon from "@assets/images/Icon.png";
import PromotionCard from "@components/extra/sales-order/PromotionCard";
import AuthComponent from "@components/auth/component";
import { roundedValue } from "@utils/general/celi-value";
import { useLazyGetAllWarehouseProductListQuery } from "@states/common/common.api";
import { DIRECT_SALES_ORDER } from "@utils/constants/direct-sales-order.constants";

type FormFields = z.infer<typeof saleOrderSchema>;

const DirectSalesOrderEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const productBarcodeRef = useRef(null);
  const tableContainerRef = useRef<any>();
  const [businessUnits, setBusinessUnits] = useState<any>([]);
  const { events } = useDraggable(tableContainerRef);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [autoSelectedRangeByAmount, setAutoSelectedRangeByAmount] =
    useState("0");
  const [autoSelectedRangeByQty, setAutoSelectedRangeByQty]: any =
    useState("0");
  const [baseQty, setBaseQty]: any = useState(1);
  const [selectedPromotionItemList, setSelectedPromotionItemList] = useState(
    []
  );

  const [customerChannelId, setCustomerChannleId] = useState("");
  const [wayId, setWayId] = useState("");
  const {
    state: { status, payment_type_id },
  } = useLocation();
  const [paymentTermDisabled, setPaymentTermDisabled] = useState(false);
  const user = useSelector(selectCurrentUser);
  const storedUser = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_KEY);
  const userData = user || (storedUser ? JSON.parse(storedUser!) : null);
  const { data: userWarehouseData, error: fetchUserWarehousesError } =
    useGetWarehousesByUserQuery({
      user_id: userData?.user_id,
    });
  const [getAllWarehouseProductList, { data: warehouseProductData }] =
    useLazyGetAllWarehouseProductListQuery();
  const [
    getAllWayWarehouse,
    { data: wayWarehouseData, error: fetchWayWarehouseError },
  ] = useLazyGetAllWayWarehouseQuery();

  const { productData: productGetAllData } = useGetAllProducts({
    limit: 10000,
  });

  const { formValues } = useSelector((state: RootState) => state.common);

  const {
    secondarySalesOrderData,
    secondarySalesOrderLoading,
    secondarySalesOrderRefetch,
  } = useGetOneSecondarySalesorder(id!);

  const defaultValues = {
    secondary_sales_order_id: secondarySalesOrderData?.secondary_sales_order_id,
    sales_date: secondarySalesOrderData?.sales_date,
    delivery_date: secondarySalesOrderData?.delivery_date,
    sale_promotion_id: secondarySalesOrderData?.sale_promotion_id,
    //@ts-ignore
    promotion_name: secondarySalesOrderData?.promotion_name,
    customer_id: secondarySalesOrderData?.customer_id,
    business_unit_id: secondarySalesOrderData?.business_unit_id,
    payment_type_id: secondarySalesOrderData?.payment_type_id,
    sale_type_id: secondarySalesOrderData?.sale_type_id,
    payment_terms_id: secondarySalesOrderData?.payment_terms_id,
    customer_name:
      secondarySalesOrderData?.customer_first_name +
      " " +
      (secondarySalesOrderData?.customer_last_name || "..."),
    product_detail: secondarySalesOrderData?.product_detail,
    sub_total: secondarySalesOrderData?.sub_total,
    grand_total_amount: secondarySalesOrderData?.grand_total_amount,
    discount_amount: secondarySalesOrderData?.discount_amount,
    tax_amount: secondarySalesOrderData?.tax_amount,
    other_charges: secondarySalesOrderData?.other_charges,
    discount_type: secondarySalesOrderData?.discount_type,
    tax_type: secondarySalesOrderData?.tax_type,
    tax: secondarySalesOrderData?.tax,
    discount: secondarySalesOrderData?.discount,
    description: secondarySalesOrderData?.description,
    status: secondarySalesOrderData?.status,
  };
  console.log("defaultValues===>", defaultValues);
  const {
    register,
    handleSubmit,
    setError,
    watch,
    setValue,
    getValues,
    control,
    formState: { errors, isSubmitting }, //TODO: need to handle wrong field name errors, cause they are uncaught
    reset,
  } = useForm<FormFields>({
    defaultValues,
    resolver: zodResolver(saleOrderSchema),
  });
  console.log("secondarySalesOrderData===>", secondarySalesOrderData);
  const { append, remove, update, fields } = useFieldArray({
    control,
    name: "product_detail",
  });
  const { data: userCustomerData, error: fetchUserCustomerError } =
    useGetCustomerByUserQuery({
      user_id: userData?.user_id,
    });
  const [
    updatePrimarySaleOrder,
    { isLoading, error: updateError, isSuccess, data, reset: updateReset },
  ] = useUpdateSecondarySaleOrderMutation();
  const { customerData } = useGetAllCustomers({ limit: 1000 });
  const [getOneCustomer, { data: getOneCustomerData }] =
    useLazyGetCustomerQuery();
  const { data: paymentTermData } = useGetAllPaymentTermsQuery({ status: 1 });
  const { paymentTypeData } = useGetAllPaymentTypes();
  const { data: saleTypeData } = useGetAllSaleTypeQuery();
  const [customerType, setCustomerType] = useState<any>();
  const [watchLoading, setWatchLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const [
    getProductDetailByWarehouse,
    {
      data: productDetailByWarehouseData,
      error: productDetailByWarehouseError,
      isLoading: productDetailByWarehouseLoading,
    },
  ] = useLazyGetProductDetailByWarehouseQuery();
  const [
    getAllPromotionProductList,
    {
      data: promotionProductData,
      error: promotionProductDataError,
      isLoading: promotionProductDataLoading,
    },
  ]: any = useLazyGetAllPromotionProductListQuery();
  const [
    getAllPromotionNameList,
    {
      data: PromotionData,
      error: getAllPromotioDataError,
      isLoading: promotioDataLoading,
    },
  ]: any = useLazyGetAllPromotionNameListQuery();
  const [
    getProductByBarcode,
    { data: productData, isLoading: productLoading },
  ] = useLazyGetProductByBarcodeQuery();

  useErrorHandling(
    ...castTypeArr<TFailResponse>([
      updateError,
      promotionProductDataError,
      getAllPromotioDataError,
      productDetailByWarehouseError,
    ])
  );
  useEffect(() => {
    if (wayId) {
      getAllWayWarehouse({
        way_id: wayId,
      });
    }
  }, [wayId]);
  useEffect(() => {
    if (formValues?.payment_type_id === 3) {
      //@ts-ignore
      // setValue('payment_terms_id', undefined);

      setPaymentTermDisabled(true);
      setValue("payment_terms_id", undefined);
    } else {
      setPaymentTermDisabled(false);
    }
  }, [formValues?.payment_type_id]);
  useEffect(() => {
    //@ts-ignore
    if (wayWarehouseData?.data?.length > 0) {
      getAllWarehouseProductList({
        //@ts-ignore
        warehouse_id: wayWarehouseData?.data?.[0]?.warehouse_id,
      });
    }
  }, [wayWarehouseData]);
  useEffect(() => {
    if (formValues?.customer_id) {
      getOneCustomer({
        id: formValues?.customer_id,
      });
    }
  }, [formValues?.customer_id]);

  useEffect(() => {
    if (getOneCustomerData?.data) {
      setBusinessUnits(getOneCustomerData?.data?.business_units || []);
    }
  }, [getOneCustomerData]);

  useEffect(() => {
    if (formValues?.customer_id && userCustomerData) {
      //@ts-ignore
      const customer = userCustomerData?.data.find(
        (c: any) => c.customer_id == formValues?.customer_id
      );
      if (customer) {
        setCustomerChannleId(customer.customer_category_id);
        setCustomerType(customer?.customer_type_id);
      }

      if (customer?.business_units && customer.business_units.length > 0) {
        setBusinessUnits(customer?.business_units || []);
      }
    }
  }, [formValues?.customer_id, userCustomerData]);
  useEffect(() => {
    if (formValues?.business_unit_id) {
      let filterBusinessUnit = businessUnits.find((bu: any) => {
        return bu.business_unit_id == formValues?.business_unit_id;
      });
      if (filterBusinessUnit?.way_id) {
        setWayId(filterBusinessUnit.way_id);
      }
    }
  }, [formValues?.business_unit_id, businessUnits]);

  useEffect(() => {
    if (wayId && customerChannelId && formValues?.sales_date) {
      console.log("start ");
      getAllPromotionNameList({
        customer_category_id: customerChannelId,
        way_id: wayId,
        start_date: formValues?.sales_date,
        type : 3
      });
    }
  }, [wayId, customerChannelId, formValues?.sales_date]);
  // useEffect(() => {
  //   if (!formValues?.sale_promotion_id) return; // Prevent running when empty

  //   if (defaultValues?.sale_promotion_id != formValues?.sale_promotion_id) {
  //     remove(); // Clears product details only when ID changes
  //     dispatch(removeAllProductDetail([])); // Ensure this does not trigger a re-render loop
  //   }
  // }, [formValues?.sale_promotion_id, defaultValues, remove, dispatch]); // Only relevant dependencies

  useEffect(() => {
    if (formValues?.sale_promotion_id) {
      getAllPromotionProductList({
        sale_promotion_id: formValues.sale_promotion_id,
      });
    }
  }, [formValues?.sale_promotion_id]); // Fetches products only when ID changes
  useEffect(() => {
    const fetchDetails = async () => {
      if (!defaultValues?.product_detail?.length) return;
      //@ts-ignore
      const results = await Promise.all(
        defaultValues.product_detail.map(async (dtl: any) => {
          if (dtl.product_id && dtl.warehouse_id) {
            const res = await getProductDetailByWarehouse({
              product_id: dtl.product_id,
              warehouse_id: dtl.warehouse_id,
            }).unwrap(); // if using RTK Query

            return {
              ...dtl,
              //@ts-ignore
              available_qty: res?.data?.available_qty || 0,
              //@ts-ignore
              old_available_qty: res?.data?.available_qty || 0,
            };
          }
          return dtl;
        })
      );

      console.log("All product details fetched", results);

      dispatch(addFormValues({ ...defaultValues, product_detail: results }));
      reset({ ...defaultValues, product_detail: results });
    };

    fetchDetails();
  }, [secondarySalesOrderData, reset, dispatch, getProductDetailByWarehouse]);

  const handlePromotionChange = (selectedPromotionId: any) => {
    if (formValues?.sale_promotion_id !== selectedPromotionId) {
      remove(); // Clear product details
      dispatch(removeAllProductDetail([])); // Reset product details in Redux
    }

    // Update the form state with the new promotion ID
    setValue("sale_promotion_id", selectedPromotionId);
  };
  useEffect(() => {
    if (id) {
      secondarySalesOrderRefetch();
    }
  }, [id, secondarySalesOrderRefetch]);

  useEffect(() => {
    if (isSuccess && data) {
      Toast.fire({
        title: data.message,
        icon: "success",
      });

      navigate(DIRECT_SALES_ORDER.BASE_PATH, {
        state: {
          reload: true,
        },
      });
    }
  }, [isSuccess, data, navigate, Toast]);
  useEffect(() => {
    if (!fields?.length) return;

    fields.forEach((field, index) => {
      // Find corresponding warehouse product
      //@ts-ignore
      const warehouseProduct = warehouseProductData?.data?.find(
        (p: any) => p.product_id === field.product_id
      );

      if (!warehouseProduct) return;

      // Find the selected unit for this field
      const selectedUnit = field.unit_type_data?.find(
        (u: any) => u.unit_id === field.unit_id
      ) || { base_qty: 1 };

      const base_qty = Number(selectedUnit.base_qty) || 1;

      // Total quantity in warehouse for this product
      const warehouseQty = Number(warehouseProduct.available_qty) || 0;

      // Available quantity based on the selected unit
      const available_qty =
        base_qty > 0 ? Math.floor(warehouseQty / base_qty) : warehouseQty;

      // Update field
      const newObj = {
        ...field,
        available_qty,
        old_available_qty: warehouseQty, // keep raw warehouse qty for calculations
      };

      update(index, newObj);
    });
  }, [warehouseProductData, fields, update]);

  const handleRemove = (index: number) => {
    remove(index);
    dispatch(removeProductDetail(index));
  };

  const handleAppend = () => {
    append({
      amount: 0,
      discount: 0,
      discount_amount: 0,
      discount_type: "AMT",
      unit_type_data: [],
      product_id: "",
      api_level: "",
      qty: 0,
      available_qty: 0,
      old_available_qty: 0,
      tax: 0,
      tax_amount: 0,
      tax_type: "AMT",
      total_amount: 0,
      sales_price: 0,
      unit_id: "",
      unit_name: "",
      is_promotion_item: false,
      is_promotion_item_list: [],
      product_code: "",
      unit_type_id: "",
      product_name: "",
      warehouse_id: "",
      warehouse_name: "",
    });
  };
  function calcProductTotals(product: any, useBaseQty = true) {
    const qty = Number(product.qty || 0);
    const base_qty = useBaseQty ? Number(product.base_qty || 1) : 1;
    const sale_price = Number(product.sales_price || 0);

    // 🧮 Calculate gross amount
    const gross_amount = qty * base_qty * sale_price;

    // 💸 Discount calculation
    let discount_amount = 0;
    const discountVal = Number(product.discount || 0);
    const discountType = product.discount_type || "AMT";

    if (product.is_promotion_item && product._promo_discount) {
      // Promotion discount
      const promoDiscount = Number(product._promo_discount || 0);
      const promoDiscountType = product._promo_discount_type || "AMT";

      discount_amount =
        promoDiscountType === "%"
          ? gross_amount * (promoDiscount / 100)
          : promoDiscount * qty * base_qty;
    } else {
      // Normal discount
      discount_amount =
        discountType === "%"
          ? gross_amount * (discountVal / 100)
          : Math.min(discountVal, gross_amount);
    }

    // 🧾 Tax calculation
    const taxable_amount = gross_amount - discount_amount;
    const taxVal = Number(product.tax || 0);
    const taxType = product.tax_type || "AMT";

    const tax_amount =
      taxType === "%"
        ? taxable_amount * (taxVal / 100)
        : Math.min(taxVal, taxable_amount);

    // 🔢 Final total
    const total_amount = gross_amount - discount_amount + tax_amount;

    return {
      amount: gross_amount,
      discount_amount,
      tax_amount,
      total_amount: isNaN(total_amount) ? 0 : total_amount,
    };
  }
  function applyPromotionIfAny(product: any) {
    if (!product?.is_promotion_item) return calcProductTotals(product);

    const promo = product.is_promotion_item_list;
    if (!promo || !Array.isArray(promo.info)) return calcProductTotals(product);

    const qty = Number(product.qty || 0);

    const appliedTier = promo.info.find(
      (info: any) =>
        Number(info.sale_range_from) <= qty && qty <= Number(info.sale_range_to)
    );

    if (!appliedTier) {
      product.is_promotion_item = false;
      product._promo_discount = 0;
      product._promo_discount_type = 0;
      return calcProductTotals(product);
    }

    product._promo_discount = appliedTier.discount || 0;
    product._promo_discount_type = appliedTier.discount_type || 0;
    product._promo_bonus = appliedTier.bonus || 0;
    product._promo_cashback = appliedTier.cashback || 0;

    return calcProductTotals(product);
  }
  const subscription = watch((formValues, { name }) => {
    const { product_detail, ...theRest } = formValues;
    if (!Array.isArray(product_detail)) return;

    let totalBonus = 0;
    let totalCashback = 0;

    product_detail.forEach((prod, index) => {
      let updatedProduct = { ...prod };

      if (name?.includes(`product_detail.${index}.product_id`)) {
        setValue(`product_detail.${index}.qty`, 0);
        setValue(`product_detail.${index}.amount`, 0);
        setValue(`product_detail.${index}.total_amount`, 0);
        setValue(`product_detail.${index}.discount`, 0);
        setValue(`product_detail.${index}.discount_type`, "AMT");
        setValue(`product_detail.${index}.is_promotion_item`, false);
        setValue(`product_detail.${index}.is_promotion_item_list`, null);
      }

      if (
        updatedProduct.product_id &&
        updatedProduct.unit_id &&
        promotionProductData
      ) {
        const promoDetail = promotionProductData.details.find(
          (d: any) =>
            d.product_id === updatedProduct.product_id &&
            d.unit_id === updatedProduct.unit_id
        );

        if (promoDetail) {
          updatedProduct.is_promotion_item = true;
          updatedProduct.is_promotion_item_list = { ...promoDetail };
        } else {
          updatedProduct.is_promotion_item = false;
          updatedProduct.is_promotion_item_list = null;
        }
      }

      // 🧾 Calculate totals
      const totals = applyPromotionIfAny(updatedProduct);
      updatedProduct = { ...updatedProduct, ...totals };

      //@ts-ignore
      totalBonus += Number(updatedProduct._promo_bonus || 0);
      //@ts-ignore
      totalCashback += Number(updatedProduct._promo_cashback || 0);

      //@ts-ignore
      update(index, updatedProduct);
      //@ts-ignore
      updateProductDetail({ index, product_detail: updatedProduct });
    });

    const subTotal = product_detail.reduce(
      //@ts-ignore
      (acc, cur) => acc + (cur.total_amount || 0),
      0
    );

    const discountAmount =
      theRest.discount_type === "%"
        ? subTotal * (Math.min(theRest.discount || 0, 100) / 100)
        : Math.min(theRest.discount || 0, subTotal);

    const taxAmount =
      theRest.tax_type === "%"
        ? subTotal * ((theRest.tax || 0) / 100)
        : theRest.tax || 0;

    const otherCharges = theRest.other_charges || 0;

    dispatch(
      addFormValues({
        ...theRest,
        product_detail: product_detail.map((p) => ({ ...p })),
        sub_total: subTotal,
        discount_amount: discountAmount,
        discount: theRest.discount || 0,
        tax_amount: taxAmount,
        tax: theRest.tax || 0,
        other_charges: otherCharges,
        bonus: totalBonus,
        cash_back: totalCashback,
        grand_total_amount: Math.round(
          subTotal + otherCharges + taxAmount - discountAmount
        ),
      })
    );
    return () => subscription.unsubscribe();
  });

  console.log("formValues===>", formValues);
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      // alert("hit");
      // async stuff here
      // console.log(data, 'data');
      await asyncDispatcher(data);
    } catch (error: any) {
      setError("root", {
        message: error.message,
      });
    }
  };

  const asyncDispatcher = useCallback(
    async (reqBody: any) => {
      try {
        const { product_detail, discount, tax, type, ...rest } = formValues;
        // if (
        //   formValues.payment_type_id != 3 &&
        //   formValues.payment_terms_id == null
        // ) {
        // } else {
        const trimmedValues = {
          sales_date: formValues.sales_date,
          delivery_date: formValues.delivery_date,
          sale_type_id: formValues.sale_type_id,
          bouns: formValues.bouns,
          type: "Direct",
          cash_back: formValues.cash_back,
          sale_promotion_id: formValues.sale_promotion_id,
          customer_id: formValues.customer_id,
          payment_type_id: formValues.payment_type_id,
          payment_terms_id: formValues.payment_terms_id,
          remark: formValues.remark,
          description: formValues.description,
          tax: formValues.tax,
          tax_type: formValues.tax_type,
          tax_amount: formValues.tax_amount,
          discount: formValues.discount,
          discount_type: formValues.discount_type,
          discount_amount: formValues.discount_amount,
          other_charges: formValues.other_charges,
          grand_total_amount: formValues.grand_total_amount,
          sub_total: formValues.sub_total,
        };

        const formattedProductDetails = product_detail?.map((pd: any) => ({
          product_id: pd.product_id,
          warehouse_id: pd.warehouse_id,
          unit_id: pd.unit_id,
          sales_price: pd.sales_price,
          available_qty: pd.available_qty,
          qty: pd.qty,
          amount: pd.amount,
          discount: pd.discount,
          discount_type: pd.discount_type,
          discount_amount: pd.discount_amount,
          is_promotion_item: pd.is_promotion_item,
          sale_promotion_detail_id:
            pd?.is_promotion_item_list?.sale_promotion_detail_id,
          tax: pd.tax,
          tax_type: pd.tax_type,
          tax_amount: pd.tax_amount,
          total_amount: pd.total_amount,
        }));

        await updatePrimarySaleOrder({
          id: id!,
          body: {
            ...trimmedValues,
            product_detail: formattedProductDetails,
          },
        });
        // }
      } catch (error) {
        console.log("Error", error);
      }
    },
    [updatePrimarySaleOrder, isSuccess, Toast, navigate, formValues]
  );
  console.log("errors", errors);
  return (
    <>
      <SaleOrderPromotionDetailModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        data={selectedPromotionItemList}
        amount={autoSelectedRangeByAmount}
        qty={autoSelectedRangeByQty}
        base_qty={baseQty}
      />
      <AuthComponent
        action="edit"
        module="Direct Sale Order"
        subModule="Direct Sale Order"
        page
      >
        <PageWrapper>
          {(isLoading || productLoading || secondarySalesOrderLoading) && (
            <LoadingSpinner />
          )}
          <BackButton />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="ml-2 text-2xl font-[600] text-primary-dark uppercase">
                Edit Direct Sale Order
              </h3>
              {/* {(payment_type_id == 1 && status == 3) ||
              (payment_type_id == 2 && status == 1) ||
              (payment_type_id == 3 && status == 1) ? (
                <CustomButton
                  handleClick={() => {
                    navigate(PRIMARY_SALE_ORDER.CONVERT_TO_INVOICE_PATH(id!), {
                      state: {
                        carriedFormValues: formValues,
                      },
                    });
                  }}
                  color="bg-primary-dark"
                  title="Convert To Invoice"
                  className="px-8 py-4 text-lg font-semibold"
                />
              ) : null} */}
              {status == 3 ? (
                <CustomButton
                  handleClick={() => {
                    navigate(
                      DIRECT_SALES_ORDER.CONVERT_TO_INVOICE_PATH(id!),
                      {
                        state: {
                          carriedFormValues: formValues,
                        },
                      }
                    );
                  }}
                  color="bg-primary-dark"
                  title="Convert To Invoice"
                  className="px-8 py-4 text-lg font-semibold"
                />
              ) : null}
            </div>
            <div className="grid grid-cols-6 gap-x-4 gap-y-8">
              <CustomDateField
                disabled={status == 3}
                errors={errors}
                name="sales_date"
                label="Sale Date"
                register={register}
                key="sales_date"
                required={true}
                //@ts-ignore
                control={control}
                value={secondarySalesOrderData?.sales_date}
              />
              <CustomDateField
                required
                errors={errors}
                name="delivery_date"
                label="Delivery Date"
                register={register}
                key="delivery_date"
                //@ts-ignore
                control={control}
                disabled={status == 3}
                minDate={formValues?.sales_date}
              />
              <CustomSelectField
                required
                errors={errors}
                name="sale_type_id"
                label="Sale Type"
                placeHolder="Select Sale type"
                //@ts-ignore
                control={control}
                options={saleTypeData?.map((item: any) => ({
                  label: item.sale_type_name,
                  value: item.sale_type_id,
                }))}
                value={secondarySalesOrderData?.sale_type_id}
                disabled={status == 3}
              />
              <CustomSearchModalChooseField
                disabled={status == 3 || true}
                key={"customer_id"}
                errors={errors}
                colSpan={2}
                name={"customer_id"}
                label="Customer Name"
                title={"Customer List"}
                required={true}
                columns={[
                  {
                    name: "customer_name",
                    columnName: "Customer Name",
                  },
                  {
                    name: "phone_number",
                    columnName: "Phone Number",
                  },
                ]}
                register={register}
                // borderColor={field.borderColor}
                placeHolder="Choose customer name"
                setValue={setValue}
                data={customerData?.map((customer) => ({
                  customer_id: customer.customer_id,
                  customer_name:
                    customer.customer_first_name +
                    " " +
                    (customer.customer_last_name || " "),
                  phone_number: customer.customer_phone1,
                }))}
                columnName={"Customer Name"}
                idName={"customer_id"}
                // value={field.value}
                nameName={"customer_name"}
                value={
                  secondarySalesOrderData?.customer_first_name +
                  " " +
                  (secondarySalesOrderData?.customer_last_name || " ")
                }
              />
              <CustomSelectField
                disabled={
                  getValues("customer_id") === undefined || status == 3 || true
                }
                errors={errors}
                name="business_unit_id"
                label="Business Unit"
                placeHolder="Select business unit"
                //@ts-ignore
                control={control}
                options={businessUnits?.map((bu: any) => ({
                  label: bu.business_unit_name,
                  value: bu.business_unit_id,
                }))}
                required
              />
              <CustomSelectField
                disabled={status == 3}
                errors={errors}
                required={true}
                name="payment_type_id"
                label="Payment Type"
                placeHolder="Select payment type"
                //@ts-ignore
                control={control}
                // options={paymentTypeData?.map((item: any) => ({
                //     label: item.payment_type_name,
                //     value: item.payment_type_id,
                // }))}
                options={paymentTypeData
                  ?.filter((item: any) => {
                    // For customerType 1: Include only "CashDown"
                    if (customerType == 1) {
                      return item.payment_type_name == "Cash Down";
                    }
                               if (customerType == 2) {
                    // For customerType 2: Allow both "Cash Down" and "Credit"
                        return (
                          item.payment_type_name == "Cash Down" ||
                          item.payment_type_name == "Credit"
                        );
                  }
                    // Default: Exclude "Consignment"
                    return item.payment_type_name != "Consignment";
                  })
                  .map((item: any) => ({
                    label: item.payment_type_name,
                    value: item.payment_type_id,
                  }))}
                defaultValue={secondarySalesOrderData?.payment_type_id}
              />
              <CustomSelectField
                disabled={paymentTermDisabled || status == 3}
                errors={errors}
                name="payment_terms_id"
                label="Payment Term"
                placeHolder="Select payment term"
                //@ts-ignore
                control={control}
                options={paymentTermData
                  ?.filter((item: any) => item.payment_terms != 3) // Remove item with payment_terms_id === 3
                  .map((item: any) => ({
                    label: item.payment_terms,
                    value: item.payment_terms_id,
                  }))}
                value={secondarySalesOrderData?.payment_terms_id}
                required={formValues?.payment_type_id !== 3}
              />
              {PromotionData && PromotionData.length > 0 && (
                <CustomSelectField
                  // disabled
                  errors={errors}
                  disabled={status == 3}
                  label="Promotion Name"
                  name="sale_promotion_id"
                  placeHolder="Select Promotion Name"
                  //@ts-ignore
                  control={control}
                  options={PromotionData?.map((sp: any) => ({
                    label: sp.promotion_name,
                    value: sp.sale_promotion_id,
                  }))}
                  required
                  onSelectChange={handlePromotionChange}
                />
              )}
              {/* <CustomTextAreaField
                        disabled={status == 3}
                        value={formValues?.remark}
                        key={'remark'}
                        colSpan={3}
                        errors={errors}
                        name="remark"
                        register={register}
                    /> */}
              <CustomTextAreaField
                disabled={status == 3}
                value={formValues?.description}
                key={"description"}
                errors={errors}
                colSpan={6}
                name="description"
                register={register}
              />
              <div className="flex justify-between col-span-6">
                <h3 className="ml-2 font-[600] text-primary-dark">
                  <span className=" text-[1.1rem]">Add Sale Order Detail</span>
                </h3>
              </div>
              <div className="flex justify-between col-span-6">
                <h3 className="ml-2 font-[600] text-primary-dark">
                  <span className=" text-[1.1rem]">Product Detail</span>
                  <span className="ml-1 text-red-500 text-[1.1rem]">*</span>
                </h3>
                <h4
                  onClick={status != 3 ? handleAppend : undefined}
                  className="mr-4 cursor-pointer font-[500] text-primary-dark text-[1rem]"
                >
                  Add More Product +
                </h4>
              </div>
              <div className="-mt-4 col-span-full">
                <div
                  ref={tableContainerRef}
                  {...events}
                  className="w-[100%] mx-auto cursor-grab active:cursor-grabbing overflow-x-auto hide-scrollbar"
                >
                  <table className="min-w-full overflow-x-scroll custom-table">
                    <thead className="h-12 py-3 text-xs font-light text-netural-600 bg-neutral-100">
                      <th className="text-xs font-light 2xl:text-sm ">No</th>
                      <th className="text-xs font-light 2xl:text-sm ">
                        Product Name
                      </th>

                      <th className="text-xs font-light 2xl:text-sm">Unit</th>
                      <th className="text-xs font-light 2xl:text-sm">
                        Sales Price
                      </th>
                      <th className="text-xs font-light 2xl:text-sm">Qty</th>
                      <th className="text-xs font-light text-nowrap 2xl:text-sm">
                        Available Qty
                      </th>
                      <th className="text-xs font-light 2xl:text-sm">Amount</th>
                      <th className="text-xs font-light 2xl:text-sm">
                        Discount
                      </th>
                      <th className="text-xs font-light 2xl:text-sm">Tax</th>
                      <th className="text-xs font-light 2xl:text-sm">
                        Total Amount
                      </th>
                      <th className="text-xs font-light 2xl:text-sm">Action</th>
                    </thead>
                    <tbody className="text-center">
                      {formValues?.product_detail?.length > 0 ? (
                        formValues?.product_detail?.map(
                          (product_detail: any, i: number) => {
                            const options = product_detail?.unit_type_data.map(
                              (unit: any) => {
                                return {
                                  label: unit.unit_name,
                                  value: unit.unit_id,
                                };
                              }
                            );

                            let baseUnit = product_detail?.unit_type_data.find(
                              (unit: any) => {
                                return unit.unit_id == product_detail?.unit_id;
                              }
                            );
                            let base_qty = (baseUnit && baseUnit.base_qty) || 1;
                            return (
                              <tr key={i}>
                                {/* <td>{baseUnit && baseUnit.base_qty}</td> */}
                                {product_detail?.is_promotion_item === true ? (
                                  <td>
                                    <div className="w-[120px] flex justify-center items-center ">
                                      <img
                                        src={PromotionIcon}
                                        alt="promotion_icon"
                                        className=" object-cover cursor-pointer"
                                        onClick={() => {
                                          setSelectedPromotionItemList(
                                            product_detail?.is_promotion_item_list ||
                                              []
                                          );
                                          setAutoSelectedRangeByAmount(
                                            product_detail?.amount
                                          );
                                          setAutoSelectedRangeByQty(
                                            product_detail?.qty
                                          );
                                          setBaseQty(
                                            product_detail?.base_qty || 1
                                          );
                                          setIsModalOpen(true);
                                        }}
                                      />
                                    </div>
                                  </td>
                                ) : (
                                  <td>-</td>
                                )}

                                <td>
                                  <CustomSelectField
                                    options={
                                      //@ts-ignore
                                      warehouseProductData &&
                                      //@ts-ignore
                                      warehouseProductData?.data?.length > 0 &&
                                      //@ts-ignore
                                      warehouseProductData?.data?.map(
                                        //@ts-ignore
                                        (product) => {
                                          return {
                                            label: ` (
                                            ${product.product_code_prefix}${product.product_code}-${product.product_name})`,
                                            value: product.product_id,
                                          };
                                        }
                                      )
                                    }
                                    classNames="w-[100%] min-w-[350px]"
                                    noLabel
                                    errors={errors}
                                    name={`product_detail.${i}.product_id`}
                                    //@ts-ignore
                                    control={control}
                                  />
                                  {/* @ts-ignore */}
                                  {errors?.product_detail?.[i].product_id && (
                                    <div className="mt-2 font-[300] italic text-[0.7rem] text-red-600">
                                      {
                                        errors?.product_detail?.[i]?.product_id
                                          ?.message
                                      }
                                    </div>
                                  )}
                                </td>

                                <td>
                                  <CustomSelectField
                                    options={
                                      product_detail?.unit_type_data &&
                                      product_detail?.unit_type_data.map(
                                        (unit: any) => {
                                          return {
                                            label: unit.unit_name,
                                            value: unit.unit_id,
                                          };
                                        }
                                      )
                                    }
                                    classNames="w-[100%] min-w-[300px]"
                                    noLabel
                                    errors={errors}
                                    name={`product_detail.${i}.unit_id`}
                                    //@ts-ignore
                                    control={control}
                                    value={product_detail?.unit_id}
                                    defaultValue={product_detail?.unit_id}
                                  />
                                </td>
                                <td>
                                  <CustomInputField
                                    readOnly
                                    disabled
                                    noLabel
                                    classNames="w-[100%]"
                                    errors={errors}
                                    name={`product_detail.${i}.sales_price`}
                                    register={register}
                                    setValue={setValue}
                                    value={product_detail.sales_price}
                                  />
                                </td>
                                <td>
                                  <Controller
                                    name={`product_detail.${i}.qty`}
                                    control={control}
                                    render={({ field }) => (
                                      <input
                                        {...field} // includes onChange, value, ref
                                        type="text"
                                        min={0}
                                        max={product_detail.available_qty || 0}
                                        className="min-w-[100px] w-[100%] px-4 py-3 text-black border-[0.7px] focus:ring-1 ring-primary focus:outline-none rounded border-primary-light"
                                        placeholder="Qty"
                                        onChange={(e) => {
                                          field.onChange(e);
                                          if (product_detail.is_promo_item) {
                                            setValue(
                                              `product_detail.${i}.discount`,
                                              e.target.value
                                                ? product_detail._promo_discount
                                                : 0
                                            );
                                          } else {
                                            setValue(
                                              `product_detail.${i}.discount`,
                                              e.target.value
                                                ? product_detail.discount
                                                : 0
                                            );
                                          }
                                        }}
                                      />
                                    )}
                                  />

                                  {/* @ts-ignore */}
                                  {errors?.product_detail?.[i].qty && (
                                    <div className="mt-2 font-[300] italic text-[0.7rem] text-red-600">
                                      {
                                        errors?.product_detail?.[i]?.qty
                                          ?.message
                                      }
                                    </div>
                                  )}
                                </td>
                                <td className="px-2">
                                  <div
                                    className={`px-8 py-3 text-primary-dark border-[0.7px] w-[100px] focus:ring-1 
                                                                   bg-zinc-100 cursor-not-allowed
                                                                        ring-primary focus:outline-none rounded border-primary-light
                                                                `}
                                  >
                                    {product_detail.available_qty}
                                  </div>
                                </td>
                                <td>
                                  <CustomInputField
                                    disabled
                                    inputType="number"
                                    noLabel
                                    classNames="w-[100%]"
                                    errors={errors}
                                    name={`product_detail.${i}.amount`}
                                    register={register}
                                    setValue={setValue}
                                    value={product_detail.amount}
                                  />
                                </td>
                                <td>
                                  <div className="flex items-center">
                                    {product_detail.is_promotion_item ===
                                    true ? (
                                      <CustomInputField
                                        setValue={setValue}
                                        inputType="text"
                                        classNames="-mr-3 w-[120px] px-4"
                                        noLabel
                                        errors={errors}
                                        name={`product_detail.${i}.discount`}
                                        register={register}
                                        defaultValue={product_detail.discount}
                                        value={product_detail._promo_discount}
                                        min={0}
                                        disabled={
                                          product_detail?.is_promotion_item ===
                                          true
                                        }
                                      />
                                    ) : (
                                      <CustomInputField
                                        setValue={setValue}
                                        inputType="text"
                                        classNames="-mr-3 w-[120px] px-4"
                                        noLabel
                                        errors={errors}
                                        name={`product_detail.${i}.discount`}
                                        register={register}
                                        defaultValue={product_detail.discount}
                                        value={product_detail.discount}
                                        min={0}
                                        disabled={
                                          product_detail?.is_promotion_item ===
                                          true
                                        }
                                      />
                                    )}
                                    <select
                                      disabled={
                                        product_detail?.is_promotion_item ===
                                        true
                                      }
                                      value={
                                        product_detail?.is_promotion_item ===
                                        true
                                          ? product_detail._promo_discount_type
                                          : product_detail.discount_type
                                      }
                                      onChange={(e) =>
                                        setValue(
                                          `product_detail.${i}.discount_type`,
                                          e.target.value
                                        )
                                      }
                                      className={`px-2 mx-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                                    >
                                      <option value="AMT">AMT</option>
                                      <option value="%">%</option>
                                    </select>
                                  </div>
                                </td>
                                <td>
                                  <div className="flex items-center">
                                    <CustomInputField
                                      disabled
                                      setValue={setValue}
                                      inputType="number"
                                      classNames="-mr-3 w-[130px]"
                                      noLabel
                                      errors={errors}
                                      name={`product_detail.${i}.tax`}
                                      register={register}
                                      defaultValue={product_detail.tax}
                                      value={product_detail.tax}
                                      min={0}
                                    />
                                    <select
                                      disabled
                                      value={product_detail.tax_type || "AMT"}
                                      onChange={(e) =>
                                        setValue(
                                          `product_detail.${i}.tax_type`,
                                          e.target.value
                                        )
                                      }
                                      className={`px-2 ml-2 mr-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                                    >
                                      <option value="AMT">AMT</option>
                                      <option value="%">%</option>
                                    </select>
                                  </div>
                                </td>

                                <td>
                                  <CustomInputField
                                    inputType="number"
                                    setValue={setValue}
                                    noLabel
                                    errors={errors}
                                    name={`product_detail.${i}.total_amount`}
                                    register={register}
                                    value={product_detail?.total_amount}
                                    disabled
                                  />
                                </td>
                                <td>
                                  <div
                                    style={{
                                      width: "100px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <DeleteButton
                                      // disabled={muted}
                                      id={i}
                                      actionHandler={() => handleRemove(i)}
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                        )
                      ) : (
                        <></>
                        // <tr>
                        //     <td colSpan={5} className="text-red-600 text-center italic text-sm">
                        //         Product Detail is required
                        //     </td>
                        // </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="w-[48%] 2xl:w-[36%] mt-8 float-left">
                  {promotionProductData &&
                    formValues?.product_detail?.length > 0 && (
                      <PromotionCard
                        promotionProductData={promotionProductData}
                        bonus={formValues?.bonus}
                        cash_back={formValues?.cash_back}
                      />
                    )}
                </div>
                {formValues?.product_detail?.length > 0 && (
                  <div className="bg-zinc-100 rounded w-[48%] 2xl:w-[36%] mt-8 float-right p-6">
                    <div className="flex items-center mb-4">
                      <h5 className="text-[1.05rem] basis-3/12">Sub Total</h5>
                      <span className="s basis-1/12">:</span>
                      <div className="flex items-center basis-6/12"></div>
                      <h5 className="text-right basis-2/12 text-[1.05rem]">
                        {thousandSeperator(formValues.sub_total || 0)}
                      </h5>
                    </div>
                    <div className="flex items-center mb-2">
                      <h5 className=" basis-3/12 text-[1.05rem]">Discount</h5>
                      <span className=" basis-1/12">:</span>
                      <div className="flex items-center basis-6/12">
                        <CustomInputField
                          defaultValue={0}
                          classNames="-mr-3 w-[130px]"
                          noLabel
                          inputType="number"
                          errors={errors}
                          name={"discount"}
                          placeHolder=" "
                          register={register}
                          min={0}
                          //@ts-ignore
                          max={
                            formValues?.discount_type == "%"
                              ? //@ts-ignore
                                100
                              : formValues?.sub_total
                          }
                        />
                        <select
                          {...register("discount_type")}
                          className={`px-2 ml-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                        >
                          <option selected value="AMT">
                            AMT
                          </option>
                          <option value="%">%</option>
                        </select>
                      </div>
                      <h5 className="text-right basis-2/12 text-[1.05rem]">
                        -
                        {thousandSeperator(
                          formValues.discount_amount.toFixed(2) || 0
                        )}
                      </h5>
                    </div>
                    <div className="flex items-center mb-2">
                      <h5 className=" basis-3/12 text-[1.05rem]">Tax</h5>
                      <span className=" basis-1/12">:</span>
                      <div className="flex items-center basis-6/12">
                        <CustomInputField
                          defaultValue={0}
                          classNames="-mr-3 w-[130px]"
                          noLabel
                          inputType="number"
                          errors={errors}
                          name={"tax"}
                          placeHolder=" "
                          register={register}
                          min={0}
                        />
                        <select
                          {...register("tax_type")}
                          className={`px-2 ml-2 h-12 py-3 border-[0.7px] focus:ring-1 border-primary-light ring-primary focus:outline-none rounded`}
                        >
                          <option selected value="AMT">
                            AMT
                          </option>
                          <option value="%">%</option>
                        </select>
                      </div>
                      <h5 className="text-right basis-2/12 text-[1.05rem]">
                        {thousandSeperator(
                          formValues.tax_amount.toFixed(2) || 0
                        )}
                      </h5>
                    </div>
                    <div className="flex items-center mb-4">
                      <h5 className=" basis-3/12 text-[1.05rem]">
                        Other Charges
                      </h5>
                      <span className=" basis-1/12">:</span>
                      <div className="flex items-center basis-6/12">
                        <CustomInputField
                          defaultValue={0}
                          classNames="-mr-3 w-[200px]"
                          noLabel
                          inputType="number"
                          errors={errors}
                          name={"other_charges"}
                          placeHolder=" "
                          register={register}
                        />
                      </div>
                      <h5 className="text-right basis-2/12 text-[1.05rem]">
                        {thousandSeperator(formValues.other_charges || 0)}
                      </h5>
                    </div>
                    <div className="flex items-center mb-2">
                      <h5 className="text-xl font-semibold basis-3/12">
                        Grand Total
                      </h5>
                      <span className=" basis-1/12">:</span>
                      <div className="flex items-center basis-6/12"></div>
                      <h5 className="text-xl font-semibold text-right basis-2/12 text-primary-dark">
                        {thousandSeperator(
                          formValues.grand_total_amount.toFixed(2) || 0
                        )}
                      </h5>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="ml-2 mt-14">
              <CustomButton
                isLarge
                title="Cancel"
                color="white"
                textColor="gray.800"
                className="mr-10"
                handleClick={() => navigate(-1)}
              />
              <CustomButton
                handleClick={handleSubmit(onSubmit)}
                isLarge
                title={isSubmitting ? "Loading..." : "Submit"}
                disabled={isSubmitting || isDisabled}
                type="submit"
              />
            </div>
            {errors.root && (
              <div className="mt-6 text-red-500">{errors.root.message}</div>
            )}
          </form>
        </PageWrapper>
      </AuthComponent>
    </>
  );
};

export default DirectSalesOrderEditPage;
